import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Globe2, Menu, X } from 'lucide-react';
import { useLocale } from '../i18n/LocaleProvider';

const PLAY_URL = "https://play.google.com/store/apps/details?id=tech.romindkids.kids";
const LOGO = "https://customer-assets.emergentagent.com/job_safe-studio/artifacts/mwjfvsq2_1000108957-Photoroom.png";

const navLinks = [
  { href: '/gift', key: 'gift', testId: 'gift', featured: true },
  { href: '/pricing', key: 'pricing', testId: 'pricing' },
  { href: '/features', key: 'features', testId: 'features' },
  { href: '/ecosystem', key: 'ecosystem', testId: 'ecosystem', featured: true },
  { href: '/clubs-challenges', key: 'clubs', testId: 'clubs' },
  { href: '/academy', key: 'academy', testId: 'academy' },
  { href: '/romind-lab', key: 'lab', testId: 'romind-lab', featured: true },
  { href: '/child-safety', key: 'safety', testId: 'safety' },
  { href: '/safety-research', key: 'research', testId: 'research' },
  { href: '/blog', key: 'insights', testId: 'insights' },
  { href: '/about', key: 'about', testId: 'about' },
];

const LanguageSwitcher = () => {
  const { locale, setLocale, locales, t, direction } = useLocale();
  const [open, setOpen] = useState(false);
  const active = locales.find((item) => item.code === locale) || locales[0];

  return (
    <div className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.045] px-2.5 py-2 text-[11px] font-semibold text-white/75 transition-colors hover:border-white/20 hover:bg-white/[0.08] hover:text-white"
        aria-label={t('language.label')}
        aria-expanded={open}
        data-testid="language-switcher"
      >
        <Globe2 size={13} />
        <span>{active.shortLabel}</span>
        <ChevronDown size={11} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className={`absolute ${direction === 'rtl' ? 'left-0' : 'right-0'} top-full mt-2 w-40 overflow-hidden rounded-2xl border border-white/10 bg-[#0A0912]/95 p-1.5 shadow-2xl backdrop-blur-2xl`}>
          {locales.map((item) => (
            <button
              key={item.code}
              type="button"
              onClick={() => { setLocale(item.code); setOpen(false); }}
              className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm transition-colors ${item.code === locale ? 'bg-purple-500/15 text-purple-200' : 'text-white/65 hover:bg-white/5 hover:text-white'}`}
            >
              <span>{item.label}</span>
              <span className="text-[10px] text-white/30">{item.shortLabel}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { t } = useLocale();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-black/78 backdrop-blur-2xl border-b border-white/5 shadow-2xl shadow-black/20' : 'bg-gradient-to-b from-black/45 via-black/15 to-transparent'
      }`}
    >
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="hidden md:flex flex-col py-2">
          <div className="flex items-center justify-between min-h-[42px] gap-5">
            <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2.5 group shrink-0">
              <img src={LOGO} alt="ROMIND KIDS" className="h-9 w-9 object-contain transition-transform group-hover:scale-105" />
              <span className="font-outfit text-[15px] font-bold text-white tracking-tight whitespace-nowrap">
                ROMIND <span className="text-purple-400">KIDS</span>
              </span>
            </Link>

            <div className="flex items-center gap-2 shrink-0">
              <LanguageSwitcher />
              <a
                href={PLAY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-download-cta"
                className="inline-flex items-center justify-center whitespace-nowrap rounded-full border border-fuchsia-400/50 bg-gradient-to-r from-purple-600 to-fuchsia-600 px-3.5 py-2 text-[12px] font-bold text-white shadow-lg shadow-purple-900/40 transition hover:brightness-110 lg:px-4 lg:text-sm"
              >
                {t('download.nav')}
              </a>
              <Link
                to="/contact"
                data-testid="nav-contact-cta"
                className="inline-flex items-center justify-center whitespace-nowrap px-4 lg:px-5 py-2 rounded-full text-[12px] lg:text-sm font-semibold bg-purple-600/80 border border-purple-400/40 text-white hover:bg-purple-500 hover:border-purple-400/70 transition-all duration-200 shadow-lg shadow-purple-900/30"
              >
                {t('nav.contact')}
              </Link>
            </div>
          </div>

          <div className="mt-1.5 border-t border-white/[0.055] pt-1.5">
            <div className="flex items-center justify-center gap-1 lg:gap-1.5 xl:gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`nav-${link.testId}`}
                  className={`shrink-0 whitespace-nowrap px-2 lg:px-2.5 xl:px-3 py-1.5 rounded-lg text-[10px] lg:text-[11px] xl:text-[12px] font-medium transition-all duration-200 ${
                    location.pathname === link.href
                      ? 'text-white bg-white/10'
                      : link.featured
                        ? 'text-fuchsia-200 bg-fuchsia-500/10 border border-fuchsia-400/20 hover:text-white hover:bg-fuchsia-500/20'
                        : 'text-white/58 hover:text-white hover:bg-white/[0.06]'
                  }`}
                >
                  {t(`nav.${link.key}`)}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="md:hidden flex items-center justify-between h-16">
          <Link to="/" data-testid="navbar-logo-mobile" className="flex items-center gap-2.5 group">
            <img src={LOGO} alt="ROMIND KIDS" className="h-9 w-9 object-contain transition-transform group-hover:scale-105" />
            <span className="font-outfit text-[15px] font-bold text-white tracking-tight">
              ROMIND <span className="text-purple-400">KIDS</span>
            </span>
          </Link>

          <div className="flex items-center gap-1">
            <LanguageSwitcher />
            <button
              data-testid="mobile-menu-toggle"
              className="p-2 text-white/60 hover:text-white transition-colors"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label={t('nav.toggleMenu')}
            >
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div data-testid="mobile-menu" className="md:hidden bg-black/95 backdrop-blur-2xl border-b border-white/8">
          <div className="px-4 py-4 flex flex-col gap-1.5">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`mobile-nav-${link.testId}`}
                className={`px-4 py-3 text-sm font-medium rounded-xl transition-all ${
                  link.featured
                    ? 'text-fuchsia-200 bg-fuchsia-500/10 border border-fuchsia-400/20 hover:bg-fuchsia-500/20'
                    : 'text-white/65 hover:text-white hover:bg-white/5'
                }`}
              >
                {t(`nav.${link.key}`)}
              </Link>
            ))}
            <Link
              to="/parents"
              data-testid="mobile-nav-parent-controls"
              className="px-4 py-3 text-sm font-medium text-white/65 hover:text-white hover:bg-white/5 rounded-xl transition-all"
            >
              {t('nav.parentSafety')}
            </Link>
            <a
              href={PLAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-nav-download"
              className="mt-1 rounded-xl border border-fuchsia-400/40 bg-gradient-to-r from-purple-600 to-fuchsia-600 px-4 py-3 text-center text-sm font-bold text-white transition hover:brightness-110"
            >
              {t('download.cta')}
            </a>
            <Link
              to="/contact"
              data-testid="mobile-nav-contact"
              className="px-4 py-3 text-sm font-semibold text-white bg-purple-600/80 rounded-xl text-center mt-1 transition-colors hover:bg-purple-500"
            >
              {t('nav.contact')}
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
